import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { dateTimeFormat } from 'src/app/app.constants';

type DatePickerModes = 'single' | 'range';
type AcceptedDateFormats = string | moment.Moment;
type SingleMode = moment.Moment;
type RangeMode = {
  start: moment.Moment;
  end: moment.Moment;
};
export type DatePickerOutput = SingleMode | RangeMode;
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    NzIconModule,
    NzButtonModule,
    NzSelectModule,
    NzInputNumberModule,
    NzToolTipModule,
    NzInputModule,
    NzPopoverModule,
  ],
})
export class DatePickerComponent implements OnInit, OnChanges {
  @Input() mode: DatePickerModes = 'single';
  @Input() allowSelectingBothDirections: boolean = true;
  @Input() disablePastDates: boolean = false;
  @Input() disableFutureDates: boolean = false;
  @Input() allowClear: boolean = true;
  @Input() allowToday: boolean = true;
  @Input() allowTime: boolean = false;
  @Input() allowWeekends: boolean = true;
  @Input() showDuration: boolean = true;
  @Input() fixedDuration: string | null = null;
  @Input() timePickerMode: 'dropdown' | 'manual' = 'dropdown';
  @Input() inline: boolean = false;
  @Input() size: 'large' | 'default' | 'small' = 'default';
  @Input() dateFormat = dateTimeFormat;
  @Input() minDate?: moment.Moment | string = moment();
  @Input() preselected?:
    | AcceptedDateFormats
    | [AcceptedDateFormats, AcceptedDateFormats];
  @Input() borderless = false;
  @Output() onChange = new EventEmitter<DatePickerOutput>();
  lastEmitedValue: DatePickerOutput;

  readonly weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  readonly moment = moment;
  currentMonth = moment();
  singleDate: moment.Moment | null = null;
  hoveredDate: moment.Moment | null = null;

  weeks: any[] = [];
  months = moment.months();
  years: number[] = [];

  startDate: moment.Moment | null = null;
  endDate: moment.Moment | null = null;
  startHour: string = '00';
  startMinute: string = '00';
  endHour: string = '23';
  endMinute: string = '59';

  singleHour: string = '00';
  singleMinute: string = '00';

  timePickerOptions = [
    '07:00',
    '07:30',
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
    '21:00',
    '21:30',
    '22:00',
    '22:30',
    '23:00',
    '23:30',
    '24:00',
  ];

  startTime = this.timePickerOptions[4];
  endTime = this.timePickerOptions[8];
  singleTime = this.timePickerOptions[0];

  popoverVisible: boolean = false;
  nzOpenStartTime = false;
  nzOpenEndTime = false;
  nzOpenSingleTime = false;

  get isRangeMode(): boolean {
    return this.mode === 'range';
  }

  get isSingleMode(): boolean {
    return this.mode === 'single';
  }

  get inlinePlaceholder(): string {
    if (this.isRangeMode) {
      return 'Select dates';
    } else {
      return 'Select date';
    }
  }

  get canApply(): boolean {
    if (this.allowClear) return true;
    if (this.isSingleMode) return Boolean(this.startDate);
    if (this.isRangeMode) return Boolean(this.startDate && this.endDate);
    return false;
  }

  get duration(): string {
    if (this.fixedDuration) {
      return this.fixedDuration;
    } else if (
      this.isRangeMode &&
      this.startDate &&
      this.endDate &&
      this.startTime &&
      this.endTime &&
      this.timePickerMode === 'dropdown'
    ) {
      const start = moment(this.startDate)
        .clone()
        .hour(+this.startTime.split(':')[0])
        .minutes(+this.startTime.split(':')[1]);
      const end = moment(this.endDate)
        .clone()
        .hour(+this.endTime.split(':')[0])
        .minutes(+this.endTime.split(':')[1]);

      const duration = moment.duration(end.diff(start));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();

      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } else {
      return '';
    }
  }

  ngOnInit() {
    this.initPreselected();
    this.generateCalendar();
    this.generateYears();
  }

  ngOnChanges() {
    this.initPreselected();
  }

  toggleTimePicker(arg: 'startTime' | 'endTime' | 'singleTime'): void {
    if (arg === 'startTime') {
      this.nzOpenStartTime = !this.nzOpenStartTime;
      if (this.nzOpenStartTime) {
        this.nzOpenEndTime = false;
      }
    }
    if (arg === 'endTime') {
      this.nzOpenEndTime = !this.nzOpenEndTime;
      if (this.nzOpenEndTime) {
        this.nzOpenStartTime = false;
      }
    }
    if (arg === 'singleTime') {
      this.nzOpenSingleTime = !this.nzOpenSingleTime;
    }
  }

  formatSingleDate(): string {
    if (this.lastEmitedValue) {
      return this.moment(this.lastEmitedValue as SingleMode).format(
        this.dateFormat
      );
    } else {
      return '';
    }
  }

  formatStartDate(): string {
    if ((this.lastEmitedValue as RangeMode)?.start) {
      return moment((this.lastEmitedValue as RangeMode)?.start).format(
        this.dateFormat
      );
    } else {
      return '';
    }
  }

  formatEndDate(): string {
    if ((this.lastEmitedValue as RangeMode)?.end) {
      return moment((this.lastEmitedValue as RangeMode)?.end).format(
        this.dateFormat
      );
    } else {
      return '';
    }
  }

  isDisabled(day: moment.Moment): boolean {
    if (!this.allowWeekends && this.isWeekend(day)) {
      return true;
    }

    if (this.allowSelectingBothDirections) {
      return false;
    } else {
      // When selecting start, enforce min/max rules
      if (!this.startDate || (this.startDate && this.endDate)) {
        return this.isDisabledForStart(day);
      }

      // When selecting end, allow same-day or after start
      if (this.startDate && !this.endDate) {
        if (day.isBefore(this.startDate, 'day')) return true;
        const max = this.getEffectiveMaxDate();
        if (max && day.isAfter(max, 'day')) return true;
      }

      return false;
    }
  }

  selectDate(day: moment.Moment): void {
    if (this.isDisabled(day)) return;

    if (this.isSingleMode) {
      this.singleDate = day
        .clone()
        .hour(+this.singleHour)
        .minute(+this.singleMinute);
      this.onChange.emit(this.singleDate);
      this.lastEmitedValue = this.singleDate;
    } else {
      if (!this.startDate || (this.startDate && this.endDate)) {
        if (
          !this.allowSelectingBothDirections &&
          this.isDisabledForStart(day)
        ) {
          return;
        }
        this.startDate = day.clone();
        this.endDate = null;
        this.hoveredDate = null;
      } else if (this.startDate && !this.endDate) {
        if (
          !this.allowSelectingBothDirections &&
          day.isBefore(this.startDate, 'day')
        ) {
          return;
        }
        this.endDate = day.clone();
        this.hoveredDate = null;
      }

      if (this.allowSelectingBothDirections && this.startDate && this.endDate) {
        if (this.startDate.isAfter(this.endDate)) {
          const swap = { ...{ end: this.startDate, start: this.endDate } };
          this.startDate = swap.start;
          this.endDate = swap.end;
        }
      }
    }
  }

  previousMonth(): void {
    this.currentMonth.subtract(1, 'month');
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth.add(1, 'month');
    this.generateCalendar();
  }

  /** Check if a day is in the current visible month */
  isCurrentMonth(day: moment.Moment): boolean {
    return (
      day.month() === this.currentMonth.month() &&
      day.year() === this.currentMonth.year()
    );
  }

  /** Dropdown change handlers */
  changeMonth(monthIndex: number) {
    this.currentMonth.month(monthIndex);
    this.generateCalendar();
  }

  changeYear(year: number) {
    this.currentMonth.year(year);
    this.generateCalendar();
  }

  /** Finish for range mode */
  apply() {
    if (this.isRangeMode) {
      let start, end;

      if (this.startDate && this.endDate) {
        if (this.timePickerMode === 'dropdown') {
          start = this.startDate
            .clone()
            .hour(+this.startTime.split(':')[0])
            .minute(+this.startTime.split(':')[1]);
          end = this.endDate
            .clone()
            .hour(+this.endTime.split(':')[0])
            .minute(+this.endTime.split(':')[1]);
        } else {
          start = this.startDate
            .clone()
            .hour(+this.startHour)
            .minute(+this.startMinute);
          end = this.endDate
            .clone()
            .hour(+this.endHour)
            .minute(+this.endMinute);
        }
      } else {
        start = null;
        end = null;
      }

      this.onChange.emit({ start, end });
      this.lastEmitedValue = { start, end };
    }

    if (this.inline) {
      this.popoverVisible = false;
      this.nzOpenStartTime = false;
      this.nzOpenEndTime = false;
      this.nzOpenSingleTime = false;
    }
  }

  onDayHover(day: moment.Moment): void {
    if (this.isRangeMode && this.startDate && !this.endDate) {
      this.hoveredDate = day;
    }
  }

  onDayLeave(): void {
    this.hoveredDate = null;
  }

  isWeekend(day: moment.Moment): boolean {
    return day.day() === 0 || day.day() === 6;
  }

  /** Is in hover range (only future days after startDate) */
  isInHoverRange(day: moment.Moment): boolean {
    if (
      this.mode !== 'range' ||
      !this.startDate ||
      this.endDate ||
      !this.hoveredDate
    ) {
      return false;
    }

    if (this.allowSelectingBothDirections) {
      return (
        day.isBetween(this.startDate, this.hoveredDate, 'day', '[]') ||
        day.isBetween(this.hoveredDate, this.startDate, 'day', '[]')
      );
    } else if (this.hoveredDate.isAfter(this.startDate)) {
      // Only allow hover forward in time
      return day.isBetween(this.startDate, this.hoveredDate, 'day', '[]');
    }

    return false;
  }

  isInRange(day: moment.Moment): boolean {
    const isBetweenStartAndEndDate = Boolean(
      this.startDate &&
        this.endDate &&
        day.isBetween(this.startDate, this.endDate, 'day', '[]')
    );
    return isBetweenStartAndEndDate;
  }

  isStartDay(day: moment.Moment): boolean {
    return this.isRangeMode ? day.isSame(this.startDate, 'day') : false;
  }

  isEndDay(day: moment.Moment): boolean {
    return this.isRangeMode ? day.isSame(this.endDate, 'day') : false;
  }

  goToday() {
    this.currentMonth = moment();
    this.generateCalendar();

    if (this.isSingleMode) {
      this.selectDate(moment());
    } else {
      const today = moment().startOf('day');
      this.startDate = today.clone();
      this.endDate = today.clone();
      this.hoveredDate = null;
    }
  }

  /** Quick action: clear selection */
  clearSelection() {
    this.singleDate = null;
    this.startDate = null;
    this.endDate = null;
    this.hoveredDate = null;

    if (this.isSingleMode) {
      this.onChange.emit(null);
      this.lastEmitedValue = null;
    }
  }

  /** Clamp values and adjust end time */
  validateStartTime() {
    if (this.timePickerMode === 'dropdown') {
      if (this.endTime && this.isBeforeStartTime(this.endTime)) {
        const indexOfStartTime = this.timePickerOptions.indexOf(this.startTime);
        this.endTime = this.timePickerOptions[indexOfStartTime + 1];
      }
    } else {
      let h = parseInt(this.startHour, 10);
      let m = parseInt(this.startMinute, 10);

      h = isNaN(h) ? 0 : Math.min(Math.max(h, 0), 23);
      m = isNaN(m) ? 0 : Math.min(Math.max(m, 0), 59);

      this.startHour = this.padZero(h);
      this.startMinute = this.padZero(m);
      this.adjustEndAfterStart();
    }
  }

  validateEndTime() {
    let h = parseInt(this.endHour, 10);
    let m = parseInt(this.endMinute, 10);

    h = isNaN(h) ? 0 : Math.min(Math.max(h, 0), 23);
    m = isNaN(m) ? 0 : Math.min(Math.max(m, 0), 59);

    this.endHour = this.padZero(h);
    this.endMinute = this.padZero(m);

    // Ensure end >= start + 1 minute
    this.adjustEndAfterStart();
  }

  validateSingleTime() {
    if (this.timePickerMode === 'dropdown') {
      this.singleDate = this.singleDate
        .clone()
        .hour(+this.singleTime.split(':')[0])
        .minute(+this.singleTime.split(':')[1]);
    } else {
      let h = parseInt(this.singleHour, 10);
      let m = parseInt(this.singleMinute, 10);

      if (isNaN(h) || h < 0) h = 0;
      if (h > 23) h = 23;
      if (isNaN(m) || m < 0) m = 0;
      if (m > 59) m = 59;

      this.singleHour = this.padZero(h);
      this.singleMinute = this.padZero(m);

      this.singleDate = this.singleDate
        .clone()
        .hour(+this.singleHour)
        .minute(+this.singleMinute);
    }

    this.onChange.emit(this.singleDate);
    this.lastEmitedValue = this.singleDate;
  }

  /** Adjust end time if it’s <= start time */
  adjustEndAfterStart() {
    // Parse numeric values
    let startH = parseInt(this.startHour, 10) || 0;
    let startM = parseInt(this.startMinute, 10) || 0;
    let endH = parseInt(this.endHour, 10) || 0;
    let endM = parseInt(this.endMinute, 10) || 0;

    // Convert to minutes for easy comparison
    const startTotal = startH * 60 + startM;
    const endTotal = endH * 60 + endM;

    // Ensure end >= start + 1 minute
    if (endTotal <= startTotal) {
      let newTotal = startTotal + 1;
      if (newTotal >= 24 * 60) newTotal = 24 * 60 - 1; // clamp to 23:59 max

      const newH = Math.floor(newTotal / 60);
      const newM = newTotal % 60;

      this.endHour = this.padZero(newH);
      this.endMinute = this.padZero(newM);
    }
  }

  isBeforeStartTime(timeSlot): boolean {
    const start = moment()
      .hour(+this.startTime.split(':')[0])
      .minute(+this.startTime.split(':')[1]);
    const end = moment()
      .hour(+timeSlot.split(':')[0])
      .minute(+timeSlot.split(':')[1]);

    if (this.startTime) {
      return end.isBefore(start) || end.isSame(start);
    } else {
      return false;
    }
  }

  popoverVisibilityChanged(): void {
    if (!this.popoverVisible) {
      this.nzOpenStartTime = false;
      this.nzOpenEndTime = false;
      this.nzOpenSingleTime = false;
    }
  }

  private generateCalendar() {
    const startOfMonth = this.currentMonth.clone().startOf('month');
    const dayOfWeek = startOfMonth.isoWeekday();
    const startOfGrid = startOfMonth.clone().subtract(dayOfWeek - 1, 'days');

    const endOfMonth = this.currentMonth.clone().endOf('month');
    const endDayOfWeek = endOfMonth.isoWeekday();
    const endOfGrid = endOfMonth.clone().add(7 - endDayOfWeek, 'days');

    const date = startOfGrid.clone();
    const weeks = [];

    while (date.isBefore(endOfGrid) || date.isSame(endOfGrid, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(date.clone());
        date.add(1, 'day');
      }
      weeks.push(week);
    }

    this.weeks = weeks;
  }

  private initPreselected(): void {
    if (this.preselected) {
      if (Array.isArray(this.preselected)) {
        const [start, end] = this.preselected;
        this.mode = 'range';
        this.startDate = moment(start);
        this.endDate = moment(end);
        this.startHour = this.padZero(this.startDate.hour());
        this.startMinute = this.padZero(this.startDate.minute());
        this.endHour = this.padZero(this.endDate.hour());
        this.endMinute = this.padZero(this.endDate.minute());

        if (this.timePickerMode === 'dropdown') {
          this.startTime = `${this.startHour}:${this.startMinute}`;
          this.endTime = `${this.endHour}:${this.endMinute}`;
        }

        this.lastEmitedValue = {
          start: this.startDate,
          end: this.endDate,
        };
      } else {
        this.mode = 'single';
        const date = moment(this.preselected);

        if (this.timePickerMode === 'dropdown') {
          const roundedMinute = date.minute() < 30 ? 0 : 30;
          this.singleDate = date
            .clone()
            .hour(date.hour())
            .minute(roundedMinute);
          this.singleTime = `${this.padZero(date.hour())}:${this.padZero(roundedMinute)}`;
        } else {
          this.singleDate = date.clone();
          this.singleHour = this.padZero(date.hour());
          this.singleMinute = this.padZero(date.minute());
        }

        this.currentMonth = this.singleDate.clone().startOf('month');
        this.lastEmitedValue = this.singleDate;
      }

      // Set the calendar view to the month of the preselected date
      if (this.isSingleMode && this.singleDate) {
        this.currentMonth = this.singleDate.clone().startOf('month');
      } else if (this.isRangeMode && this.startDate) {
        this.currentMonth = this.startDate.clone().startOf('month');
      }
    }
  }

  private generateYears(): void {
    const currentYear = moment().year();
    const threshold = 10;
    this.years = [];
    for (let y = currentYear - threshold; y <= currentYear + threshold; y++) {
      this.years.push(y);
    }
  }

  private padZero(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  private getEffectiveMaxDate(): moment.Moment | null {
    if (!this.disableFutureDates) return null;
    return moment().endOf('day');
  }

  private getEffectiveMinDate(): moment.Moment | null {
    if (!this.disablePastDates) return null;
    return moment().startOf('day');
  }

  private isDisabledForStart(day: moment.Moment): boolean {
    const min = this.getEffectiveMinDate();
    const max = this.getEffectiveMaxDate();

    if (min && day.isBefore(min, 'day')) return true;
    if (max && day.isAfter(max, 'day')) return true;
    return false;
  }
}
